import React, { useEffect, useState } from "react";
import { 
  Edit, Trash2, Plus, Building, User, RefreshCw, 
  Search, Filter, Download, Eye,
  Loader2, AlertCircle, Package, Scale, Calculator 
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { toast } from 'react-toastify';
import axios from "axios";
import { useAuth } from "../../contexts/AuthContext";
import "./ProductUomConversionsList.css";

const url = process.env.REACT_APP_BACKEND_URL;

const ProductUomConversionsList = () => {
  const { currentUser, userType, company } = useAuth();
  const [conversions, setConversions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterCompany, setFilterCompany] = useState("");
  const [filterProduct, setFilterProduct] = useState("");
  const [filterStatus, setFilterStatus] = useState("active");
  const [companies, setCompanies] = useState([]);
  const [products, setProducts] = useState([]);
  const [selectedConversion, setSelectedConversion] = useState(null);
  const [showDetails, setShowDetails] = useState(false);
  const navigate = useNavigate();

  // Calculate user permissions
  const isSystemAdminUser = () => {
    return userType === 'system' && (currentUser?.role === 'developer' || currentUser?.role === 'admin');
  };
  
  const isCompanyAdminUser = () => {
    return userType === 'company' && (currentUser?.role === 'admin' || currentUser?.role === 'manager');
  };

  const fetchConversions = async () => {
    try {
      setLoading(true);
      
      const params = {};
      
      // Add company filter for system admins
      if (isSystemAdminUser() && filterCompany) {
        params.company_id = filterCompany;
      }
      
      // Add product filter
      if (filterProduct) {
        params.product_id = filterProduct;
      }
      
      // Add status filter
      if (filterStatus) {
        params.status = filterStatus;
      }
      
      const res = await axios.get(`${url}/api/puomc`, { params });
      
      if (res.data.success) {
        setConversions(res.data.data || []);
      } else {
        toast.error(res.data.message || "Failed to fetch UOM conversions");
      }
    } catch (error) {
      toast.error("Failed to fetch UOM conversions");
    } finally {
      setLoading(false);
    }
  };

  const fetchCompanies = async () => {
    try {
      const response = await axios.get(`${url}/api/company`);
      
      if (response.data.success) {
        setCompanies(response.data.data || []);
      }
    } catch (error) {
      // Silent fail
    }
  };

  const fetchProducts = async (companyId) => {
    try {
      const params = companyId ? { company_id: companyId } : {};
      const response = await axios.get(`${url}/api/products/dropdown`, { params });
      
      if (response.data.success) {
        setProducts(response.data.data || []);
      }
    } catch (error) {
      // Silent fail
    }
  };

  useEffect(() => {
    if (isSystemAdminUser()) {
      fetchCompanies();
    } else if (userType === 'company' && company) {
      fetchProducts(company._id || company);
    }
    fetchConversions();
  }, []);

  useEffect(() => {
    fetchConversions();
  }, [filterCompany, filterProduct, filterStatus]);

  // Helper function to get creator display name
  const getCreatorDisplay = (conversion) => {
    if (!conversion.created_by) {
      return "System";
    }
    
    // If created_by is an object
    if (typeof conversion.created_by === 'object') {
      // Try different possible user identifier fields
      const nameFields = [
        'name', 'Name', 'NAME',
        'Usercomp_sure_name', 'Usercomp_surename',
        'username', 'userName', 'UserName',
        'full_name', 'fullName', 'FullName',
        'display_name', 'displayName', 'DisplayName',
        'first_name', 'firstName', 'First_name',
        'sure_name', 'surename', 'SureName'
      ];
      
      for (const field of nameFields) {
        if (conversion.created_by[field] && 
            conversion.created_by[field] !== "N/A" && 
            conversion.created_by[field] !== "System") {
          return conversion.created_by[field];
        }
      }
      
      // Check email fields
      const emailFields = ['email', 'Email', 'EMAIL', 'Usercomp_email', 'user_email'];
      for (const field of emailFields) {
        if (conversion.created_by[field] && conversion.created_by[field] !== "N/A") {
          const email = conversion.created_by[field];
          const username = email.split('@')[0];
          return username;
        }
      }
      
      // If all else fails, check any string value that's not "N/A" or "System"
      for (const [key, value] of Object.entries(conversion.created_by)) {
        if (typeof value === 'string' && 
            value !== "N/A" && 
            value !== "System" && 
            !/^\d{4}-\d{2}-\d{2}/.test(value) && // Not a date
            !/^[0-9a-fA-F]{24}$/.test(value)) { // Not an ObjectId
          return value;
        }
      }
      
      return "System";
    }
    
    // If created_by is a string
    if (typeof conversion.created_by === 'string') {
      if (conversion.created_by === "N/A" || conversion.created_by === "System") {
        return "System";
      }
      // Check if it's an ObjectId
      if (/^[0-9a-fA-F]{24}$/.test(conversion.created_by)) {
        return "User";
      }
      return conversion.created_by;
    }
    
    return "System";
  };

  // Helper function to get creator email
  const getCreatorEmail = (conversion) => {
    if (!conversion.created_by || typeof conversion.created_by !== 'object') return "N/A";
    
    const emailFields = ['email', 'Email', 'EMAIL', 'Usercomp_email', 'user_email'];
    
    for (const field of emailFields) {
      if (conversion.created_by[field] && conversion.created_by[field] !== "N/A") {
        return conversion.created_by[field];
      }
    }
    
    return "N/A";
  };

  const handleDelete = async (id, productName) => {
    if (window.confirm(`Are you sure you want to delete this UOM conversion for "${productName}"?`)) {
      try {
        const res = await axios.delete(`${url}/api/puomc/${id}`);
        
        if (res.data.success) {
          toast.success("UOM conversion deleted successfully");
          fetchConversions();
        } else {
          toast.error(res.data.message || "Failed to delete conversion");
        }
      } catch (error) {
        toast.error(error.response?.data?.message || "Failed to delete conversion");
      }
    }
  };

  const viewDetails = (conversion) => {
    setSelectedConversion(conversion);
    setShowDetails(true);
  };

  const filteredConversions = conversions.filter(
    (conversion) =>
      conversion.product_id?.product_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      conversion.from_uom_id?.uom_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      conversion.to_uom_id?.uom_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      conversion.product_id?.product_code?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      conversion.conversion_rate?.toString().includes(searchTerm)
  );

  const exportToCSV = () => {
    const headers = ["Product", "Product Code", "From UOM", "To UOM", "Conversion Rate", "Company", "Status", "Created By", "Created At"];
    const csvData = [
      headers.join(","),
      ...filteredConversions.map(conv => [
        `"${conv.product_id?.product_name || ''}"`,
        `"${conv.product_id?.product_code || ''}"`,
        `"${conv.from_uom_id?.uom_name || ''}"`,
        `"${conv.to_uom_id?.uom_name || ''}"`,
        conv.conversion_rate,
        `"${conv.company_id?.company_name || ''}"`,
        `"${conv.status || ''}"`,
        `"${getCreatorDisplay(conv)}"`,
        `"${new Date(conv.created_at).toLocaleDateString()}"`
      ].join(","))
    ].join("\n");
    
    const blob = new Blob([csvData], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "uom_conversions.csv";
    a.click();
    window.URL.revokeObjectURL(url);
  };

  if (loading) {
    return (
      <div className="loading-container">
        <Loader2 className="animate-spin" size={32} />
        <p>Loading conversions...</p>
      </div>
    );
  }

  return (
    <div className="puomc-container">
      {/* Header */}
      <div className="header">
        <div>
          <h2>
            <RefreshCw size={24} className="mr-3" />
            Product UOM Conversions
          </h2>
          {!isSystemAdminUser() && company && (
            <p className="company-info">
              <Building size={14} /> {company.company_name} ({company.company_code})
            </p>
          )}
        </div>
        <button onClick={() => navigate("/AddProductUomConversions")} className="add-button">
          <Plus size={16} /> Add Conversion
        </button>
      </div>

      {/* Stats Bar */}
      <div className="stats-bar">
        <div className="stats-item">
          <Package size={16} />
          <span>Total: {conversions.length} conversions</span>
        </div>
        <div className="stats-item">
          <User size={16} />
          <span>Current User: {currentUser?.name || currentUser?.Usercomp_sure_name || "You"}</span>
        </div>
        <div className="stats-item">
          <Calculator size={16} />
          <span>Showing: {filteredConversions.length} filtered</span>
        </div>
      </div>

      {/* Filters */}
      <div className="filters-container">
        <div className="search-box">
          <Search size={18} />
          <input
            type="text"
            placeholder="Search by product, UOM, or conversion rate..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
          {searchTerm && (
            <button className="clear-search-btn" onClick={() => setSearchTerm("")}>
              ×
            </button>
          )}
        </div>

        <div className="filter-group">
          {isSystemAdminUser() && (
            <div className="filter-item">
              <Building size={14} />
              <select
                value={filterCompany}
                onChange={(e) => {
                  setFilterCompany(e.target.value);
                  setFilterProduct("");
                }}
              >
                <option value="">All Companies</option>
                {companies.map((comp) => (
                  <option key={comp._id} value={comp._id}>
                    {comp.company_name}
                  </option>
                ))}
              </select>
            </div>
          )}

          <div className="filter-item">
            <Package size={14} />
            <select
              value={filterProduct}
              onChange={(e) => setFilterProduct(e.target.value)}
            >
              <option value="">All Products</option>
              {products.map((product) => (
                <option key={product._id} value={product._id}>
                  {product.product_name}
                </option>
              ))}
            </select>
          </div>

          <div className="filter-item">
            <Filter size={14} />
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
            >
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
              <option value="">All Status</option>
            </select>
          </div>

          <button className="export-btn" onClick={exportToCSV}>
            <Download size={14} /> Export CSV
          </button>
        </div>
      </div>

      {/* Conversion Details Modal */}
      {showDetails && selectedConversion && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h3>Conversion Details</h3>
              <button className="close-modal" onClick={() => setShowDetails(false)}>×</button>
            </div>
            <div className="modal-body">
              <div className="detail-grid">
                <div className="detail-item">
                  <strong>Product:</strong>
                  <span>{selectedConversion.product_id?.product_name} ({selectedConversion.product_id?.product_code})</span>
                </div>
                <div className="detail-item">
                  <strong>From UOM:</strong>
                  <span>{selectedConversion.from_uom_id?.uom_name} ({selectedConversion.from_uom_id?.uom_type})</span>
                </div>
                <div className="detail-item">
                  <strong>To UOM:</strong>
                  <span>{selectedConversion.to_uom_id?.uom_name} ({selectedConversion.to_uom_id?.uom_type})</span>
                </div>
                <div className="detail-item">
                  <strong>Conversion Rate:</strong>
                  <span className="rate-highlight">
                    1 {selectedConversion.from_uom_id?.uom_name} = {selectedConversion.conversion_rate} {selectedConversion.to_uom_id?.uom_name}
                  </span>
                </div>
                <div className="detail-item">
                  <strong>Company:</strong>
                  <span>{selectedConversion.company_id?.company_name} ({selectedConversion.company_id?.company_code})</span>
                </div>
                <div className="detail-item">
                  <strong>Status:</strong>
                  <span className={`status-badge ${selectedConversion.status}`}>
                    {selectedConversion.status}
                  </span>
                </div>
                <div className="detail-item">
                  <strong>Created By:</strong>
                  <span>
                    {getCreatorDisplay(selectedConversion)}
                    {getCreatorEmail(selectedConversion) !== "N/A" && ` (${getCreatorEmail(selectedConversion)})`}
                  </span>
                </div>
                <div className="detail-item">
                  <strong>Created At:</strong>
                  <span>{new Date(selectedConversion.created_at).toLocaleString()}</span>
                </div>
                {selectedConversion.updated_by && (
                  <div className="detail-item">
                    <strong>Last Updated:</strong>
                    <span>{new Date(selectedConversion.updated_at).toLocaleString()} by {getCreatorDisplay({created_by: selectedConversion.updated_by})}</span>
                  </div>
                )}
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn-secondary" onClick={() => setShowDetails(false)}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Conversions Table */}
      <div className="table-container">
        <table className="puomc-table">
          <thead>
            <tr>
              <th>Product</th>
              <th>From UOM</th>
              <th>To UOM</th>
              <th>Conversion Rate</th>
              {isSystemAdminUser() && <th>Company</th>}
              <th>Status</th>
              <th>Created By</th>
              <th style={{ textAlign: "center" }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredConversions.length === 0 ? (
              <tr>
                <td colSpan={isSystemAdminUser() ? 8 : 7} className="no-data">
                  <AlertCircle size={20} />
                  <div>
                    <p>No UOM conversions found</p>
                    {searchTerm && <small>No results for "{searchTerm}"</small>}
                  </div>
                </td>
              </tr>
            ) : (
              filteredConversions.map((conversion) => (
                <tr key={conversion._id} className={`conversion-row ${conversion.status === 'inactive' ? 'inactive' : ''}`}>
                  <td>
                    <div className="product-info">
                      <Package size={16} />
                      <div>
                        <strong>{conversion.product_id?.product_name || "N/A"}</strong>
                        <small>{conversion.product_id?.product_code}</small>
                      </div>
                    </div>
                  </td>
                  <td>
                    <div className="uom-info">
                      <Scale size={16} />
                      <div>
                        <strong>{conversion.from_uom_id?.uom_name || "N/A"}</strong>
                        <small>{conversion.from_uom_id?.uom_type}</small>
                      </div>
                    </div>
                  </td>
                  <td>
                    <div className="uom-info">
                      <Scale size={16} />
                      <div>
                        <strong>{conversion.to_uom_id?.uom_name || "N/A"}</strong>
                        <small>{conversion.to_uom_id?.uom_type}</small>
                      </div>
                    </div>
                  </td>
                  <td>
                    <div className="conversion-info">
                      <Calculator size={16} />
                      <div>
                        <strong>1 : {conversion.conversion_rate}</strong>
                        <small>
                          1 {conversion.from_uom_id?.uom_name} = {conversion.conversion_rate} {conversion.to_uom_id?.uom_name}
                        </small>
                      </div>
                    </div>
                  </td>
                  {isSystemAdminUser() && (
                    <td>
                      <div className="company-info">
                        <Building size={14} />
                        <span>{conversion.company_id?.company_name}</span>
                      </div>
                    </td>
                  )}
                  <td>
                    <span className={`status-badge ${conversion.status}`}>
                      {conversion.status}
                    </span>
                  </td>
                  <td>
                    <div className="user-info">
                      <User size={12} />
                      <div>
                        <strong>{getCreatorDisplay(conversion)}</strong>
                        <div style={{ fontSize: '11px', color: '#666', marginTop: '2px' }}>
                          {new Date(conversion.created_at).toLocaleDateString()}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="actions">
                    <div className="action-buttons">
                      <button 
                        onClick={() => viewDetails(conversion)} 
                        className="view-btn"
                        title="View Details"
                      >
                        <Eye size={14} />
                      </button>
                      <button 
                        onClick={() => navigate(`/puomc/edit/${conversion._id}`)} 
                        className="edit-btn"
                        title="Edit"
                      >
                        <Edit size={14} />
                      </button>
                      <button 
                        onClick={() => handleDelete(conversion._id, conversion.product_id?.product_name)} 
                        className="delete-btn"
                        title="Delete"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className="pagination">
        <span>Showing {filteredConversions.length} of {conversions.length} conversions</span>
      </div>
    </div>
  );
};

export default ProductUomConversionsList;